/**
 * Orchestrates the membership payment at signup (ELARA-1012).
 *
 * The shape of this state follows the shape of the payment, which since the card-first rewrite is:
 *
 *   preparing  → StartMembershipPayment, which opens a card collection (a SetupIntent). Nothing is
 *                created in Stripe that commits the member to anything.
 *   collecting → we hold the setup secret; the card fields are live and the plan cards are a
 *                CLIENT-SIDE choice — switching plan costs no round trip and creates nothing.
 *   confirming → the browser is confirming the card with Stripe (may bounce through 3DS).
 *   activating → CompleteMembershipPayment, which creates the subscription against that card.
 *   active     → entitled; signup can continue.
 *
 * What is deliberately NOT here is `stripe.confirmSetup` — that is Stripe.js, which is web-only, so
 * the surface that owns the card fields drives it and reports back through `completePayment` /
 * `failConfirmation`. Keeps this testable without a DOM and without Stripe.js.
 *
 * `activating` is a single server call rather than a wait: the subscription is created there, so its
 * status is known when the call returns. There is no polling, and no window in which a member has
 * paid but is not yet entitled — the two things that made the previous design's activation step a
 * state machine of its own.
 */

import type { AppFlowsContainer } from '#flows.js';
import type { SessionState } from './SessionState.js';
import type { UserProfileState } from './UserProfileState.js';

import type { MembershipPlanBenefit, MembershipPlanId } from '@elara/domain/membership-plans';
import type { MembershipAccess } from '@elara/domain/subscription';

import { getMembershipPlan, planRequiresPayment } from '@elara/domain/membership-plans';
import { isEntitlingStatus, membershipAccess } from '@elara/domain/subscription';

import { trackBeginCheckout } from '#lib/analytics-events.js';
import { daysUntil } from '#lib/date-utils.js';
import { createState } from '#lib/signals-utils.js';
import { formatMinorUnits } from '#lib/utils.js';

/** A plan on offer, with its list price. Shaped by `GetMembershipPlans`, not the local catalog. */
export interface OfferedPlan {
  id: MembershipPlanId;
  name: string;
  benefits: readonly MembershipPlanBenefit[];
  trialDays?: number;
  /** Absent when the plan has no provisioned price, or its price carries no single amount. */
  amount?: number;
  currency?: string;
  interval?: string;
  intervalCount?: number;
}

/**
 * "year", "month", "3 months" — the denominator in "£500 / year".
 *
 * Reads the interval off the price rather than mapping plan ids to words, which is what lets a
 * future billing-cadence experiment render correctly with no change here.
 */
function formatBillingPeriod(interval: string | undefined, intervalCount: number | undefined): string | undefined {
  if (!interval) return;
  const count = intervalCount ?? 1;
  return count === 1 ? interval : `${count} ${interval}s`;
}

/** A price split for display: the amount carries the weight, the period qualifies it. */
export interface PriceParts {
  /** "$2,499.00" */
  amount: string;
  /** "year", "month", "3 months"; absent for a price with no recurring terms. */
  period?: string;
}

/**
 * A running free trial, as the member should be told about it (ELARA-1042).
 *
 * Counted in CALENDAR days rather than 24-hour blocks, because that is the number a member counts:
 * a trial ending tomorrow morning has "1 day left" however few hours away it actually is. `0` is
 * therefore "ends today", not "already over" — an expired trial is not a trial at all and is
 * reported as `undefined`.
 */
export interface TrialCountdown {
  /** Whole calendar days until the trial ends; `0` on the last day. */
  daysRemaining: number;
  /** ISO timestamp the trial ends and the first charge is taken. */
  endsAt: string;
  /** The full sentence — "12 days left on your free trial". For the account menu. */
  label: string;
  /**
   * How much of the trial is still to run, 0–1, for a progress ring.
   *
   * Measured against the invite's trial length recorded on the profile, which is the same number
   * the server hands Stripe when it creates the trial — so no trial start has to be projected onto
   * the profile to derive it. Falls back to a full ring when no length is recorded (a trial added by
   * hand in the Stripe Dashboard): a ring that reads "plenty left" is a plain circle, where an
   * empty one would read as "about to expire" and alarm somebody with a fortnight to go.
   */
  fractionRemaining: number;
}

export type MembershipPaymentPhase =
  | 'idle'
  | 'preparing'
  | 'collecting'
  | 'confirming'
  | 'activating'
  | 'active'
  | 'failed';

const GENERIC_PREPARE_FAILURE =
  'We could not start the payment. Your account was created — sign in and we will pick this up.';

/**
 * Positional rather than an options object to match every other state factory in
 * `AppStatesContainer` — an options literal there assigns the container's loosely-typed
 * `features.flows` into a property, which trips `no-unsafe-assignment` where a positional
 * argument does not.
 *
 * @param sleep Injected so tests do not wait in real time.
 */
export function createMembershipPaymentState(
  flows: AppFlowsContainer,
  sessionState: SessionState,
  userProfileState: UserProfileState,
) {
  return createState({
    phase: 'idle' as MembershipPaymentPhase,
    /**
     * SetupIntent secret the card fields confirm against. Never logged, never persisted.
     *
     * A SetupIntent, not a payment: the card is collected first and the subscription is created from
     * it afterwards, which is why nothing here depends on the plan.
     */
    clientSecret: undefined as string | undefined,
    /**
     * Customer Session secret, which is what makes the card surface offer the cards this member
     * already has alongside a new one. Never logged, never persisted, same as the setup secret.
     *
     * Undefined is a normal state, not a failure: a first-time member has no cards to list, and
     * Stripe can refuse the session. Every consumer must render a plain card form without it.
     */
    customerSessionClientSecret: undefined as string | undefined,
    error: undefined as string | undefined,
    /** True while the billing portal session is being minted, so the button can say so. */
    isOpeningPortal: false,
    /**
     * Whether a plan's trial applies to THIS member, as the server reported when the collection
     * opened — or undefined until it has. One trial per customer: a returning member is charged from
     * day one.
     *
     * Undefined rather than a guess, because the guess was visible. The plan list lands before the
     * collection does (a couple of price reads against a profile read, a history read and a
     * SetupIntent), so a default of `true` showed a returning member "Start free trial" for a beat
     * and then took it away. Until the server answers, `isTrialEligible` falls back to the profile,
     * which already knows whether this member is returning.
     */
    trialEligible: undefined as boolean | undefined,
    /** Set once `begin_checkout` has been reported, so a reopened collection does not report again. */
    hasReportedCheckout: false,

    /**
     * Which plan the member is buying — a LOCAL choice until they commit.
     *
     * Nothing in Stripe corresponds to it, so switching is instant and creates nothing. The chosen
     * id is sent once, to `CompleteMembershipPayment`, which validates it against the catalog.
     */
    planId: undefined as MembershipPlanId | undefined,

    /**
     * Plans on offer, with their list prices, as the server reported them.
     *
     * Fetched rather than read from the local catalog because the PRICES only exist in Stripe. The
     * catalog is still the authority on what is offerable — the server builds this list from it and
     * re-checks `selectable` when the membership is created, so nothing here can widen what may be
     * bought.
     */
    offeredPlans: [] as OfferedPlan[],

    /** True when there is a real choice to render rather than a single plan to state. */
    get hasPlanChoice(): boolean {
      return this.offeredPlans.length > 1;
    },

    /** The plan being bought, as the server described it, falling back to the local catalog. */
    get plan(): { name: string; benefits: readonly MembershipPlanBenefit[] } | undefined {
      const id = this.planId;
      if (!id) return;
      return this.offeredPlans.find((offered) => offered.id === id) ?? getMembershipPlan(id);
    },

    /** The selected plan's own offer row, which carries its price and trial. */
    get selectedPlan(): OfferedPlan | undefined {
      const id = this.planId;
      return id ? this.offeredPlans.find((offered) => offered.id === id) : undefined;
    },

    /**
     * Load the plans on offer. Creates nothing in Stripe, so it is safe to call on mount and before
     * knowing whether the member will proceed.
     *
     * A failure is deliberately swallowed to a warning: the plan cards are a nicety, and the screen
     * still works from the profile's recorded plan. `preparePayment` is where an unusable billing
     * config becomes a real, surfaced failure.
     */
    async loadOfferedPlans(): Promise<void> {
      try {
        const result = await flows.getMembershipPlans({});
        this.offeredPlans = result.data.plans.map((plan) => ({
          id: plan.id,
          name: plan.name,
          benefits: plan.benefits.map((benefit) => ({
            icon: benefit.icon,
            text: benefit.text,
            firstTimeOnly: benefit.firstTimeOnly ?? undefined,
          })),
          trialDays: plan.trialDays ?? undefined,
          amount: plan.amount ?? undefined,
          currency: plan.currency ?? undefined,
          interval: plan.interval ?? undefined,
          intervalCount: plan.intervalCount ?? undefined,
        }));
        // Default the selection: the plan the invite granted, else the only thing on offer.
        this.planId ??= userProfileState.profile?.subscription?.plan ?? this.offeredPlans[0]?.id;
      } catch (error) {
        console.warn('[MembershipPaymentState] Could not load the membership plans', error);
        this.planId ??= userProfileState.profile?.subscription?.plan;
      }
    },

    /** Choose a plan. Local: no Stripe object corresponds to a choice until the card is confirmed. */
    selectPlan(planId: MembershipPlanId) {
      if (this.isBusy) return;
      this.planId = planId;
    },

    /**
     * The list price of an offered plan as its two parts - "$2,499.00" and "year" - so a view can
     * weight them differently: the amount is what a member compares, the period qualifies it.
     */
    listPrice(plan: OfferedPlan): PriceParts | undefined {
      if (plan.amount === undefined || !plan.currency) return;
      return {
        amount: formatMinorUnits(plan.amount, plan.currency),
        period: formatBillingPeriod(plan.interval, plan.intervalCount),
      };
    },

    /** The list price of an offered plan as one string - "$2,499.00 / year" - for text surfaces. */
    listPriceLabel(plan: OfferedPlan): string | undefined {
      const price = this.listPrice(plan);
      if (!price) return;
      return price.period ? `${price.amount} / ${price.period}` : price.amount;
    },

    /** The selected plan's price as its two parts. See {@link listPrice}. */
    get price(): PriceParts | undefined {
      const plan = this.selectedPlan;
      return plan ? this.listPrice(plan) : undefined;
    },

    /**
     * The recurring commitment for the selected plan — "$2,499.00 / year".
     *
     * The plan's list price, read from its Stripe Price via `GetMembershipPlans`. It is what the
     * member will be charged: this build configures no tax and no coupons, so the price IS the
     * invoice. Should either ever be enabled, quote `invoices.createPreview` from the server
     * instead — the figure on screen must never be one Stripe will not take.
     */
    get priceLabel(): string | undefined {
      const plan = this.selectedPlan;
      return plan ? this.listPriceLabel(plan) : undefined;
    },

    /**
     * The trial a plan would give THIS member — the invite's trial, which the server reports only on
     * the plan it was granted for, or nothing for a member who has held a membership before. Every trial read goes through here so no card, label or
     * button can promise a trial the server will withhold.
     */
    trialDaysFor(plan: OfferedPlan): number | undefined {
      return this.isTrialEligible ? plan.trialDays : undefined;
    },

    /**
     * The benefits to show THIS member. A returning member has already had what a first-time-only
     * benefit offers (the first-trip guarantee), so the card must not promise it again.
     */
    benefitsFor(benefits: readonly MembershipPlanBenefit[]): readonly MembershipPlanBenefit[] {
      return this.isReturning ? benefits.filter((benefit) => !benefit.firstTimeOnly) : benefits;
    },

    /**
     * The server's answer once it has one; the profile's until then.
     *
     * The two agree in every ordinary case — the profile says "returning" exactly when Stripe has a
     * prior membership — so the first frame is right and nothing flickers. They differ only when
     * the profile is stale, and there the server's answer wins as soon as it lands.
     */
    get isTrialEligible(): boolean {
      return this.trialEligible ?? !this.isReturning;
    },

    /** What the card is charged on confirmation. Zero on a trial, so absent there. */
    get amountDueNowLabel(): string | undefined {
      const plan = this.selectedPlan;
      if (!plan || this.trialDaysFor(plan) || plan.amount === undefined || !plan.currency) return;
      return formatMinorUnits(plan.amount, plan.currency);
    },

    /** How many days the selected plan trials for THIS member, or undefined when it does not. */
    get trialDays(): number | undefined {
      const plan = this.selectedPlan;
      return plan ? this.trialDaysFor(plan) : undefined;
    },

    /** True while the member should see a spinner rather than a form or a result. */
    get isBusy(): boolean {
      return this.phase === 'preparing' || this.phase === 'confirming' || this.phase === 'activating';
    },

    /**
     * Whether this member must pay before reaching the app.
     *
     * Reads the shared domain predicate off the profile rather than anything client-side, so the
     * gate cannot drift from what the server's flow gate will enforce. Deliberately false while
     * either the session or the profile is still loading: a redirect fired on absent-because-loading
     * data would bounce every member through the payment screen on each cold start.
     *
     * Also stays false once `active`, so the screen releases the member the moment payment lands
     * without waiting for the profile listener to catch up.
     */
    get requiresPayment(): boolean {
      return this.access?.kind === 'needs_subscription';
    },

    /**
     * True for a member coming back after cancelling — same card form as a first-timer, but the
     * screen should say so. Read off the profile rather than the server's trial answer so the
     * heading is right from the first frame, not after the collection has loaded.
     */
    get isReturning(): boolean {
      const access = this.access;
      return access?.kind === 'needs_subscription' && access.returning;
    },

    /** True for a member who WAS a member: their route is the billing portal, not the card form. */
    get hasLapsed(): boolean {
      return this.access?.kind === 'lapsed';
    },

    /** True for a comped membership that is over. No self-serve remedy; the screen says to get in touch. */
    get hasEnded(): boolean {
      return this.access?.kind === 'ended';
    },

    /**
     * True when the member must be held on the payment page, for EITHER remedy. This is what the
     * route gate reads; the page itself decides between the card form and the portal. Splitting the
     * gate by remedy is how a lapsed member used to fall through with no screen at all.
     */
    get isLocked(): boolean {
      const kind = this.access?.kind;
      return kind === 'needs_subscription' || kind === 'lapsed' || kind === 'ended';
    },

    /**
     * The one access answer, or undefined while it is still unknown.
     *
     * Undefined covers session or profile still loading, which is NOT the same as "nothing owed":
     * a redirect fired on absent-because-loading data would bounce every member through the payment
     * screen on each cold start. Every consumer reads this rather than deriving its own view of the
     * same fields, which is what stopped two gates disagreeing.
     */
    get access(): MembershipAccess | undefined {
      // Releases the screen the moment payment lands, without waiting for the profile listener.
      if (this.phase === 'active') return { kind: 'entitled', status: 'active' };
      if (!sessionState.isAuthenticated || sessionState.isLoading) return;
      if (userProfileState.isLoading) return;
      const profile = userProfileState.profile;
      if (!profile) return;
      return membershipAccess(profile);
    },

    /**
     * The running free trial, or undefined when there is not one (ELARA-1042).
     *
     * `currentPeriodEnd` IS the trial end while a subscription is `trialing` — Stripe sets the
     * period to the trial, and charges at the end of it — so no separate field is projected for
     * this. A `trialing` subscription with no period recorded yields nothing rather than a guess: a
     * countdown to an unknown date is worse than no countdown.
     *
     * Reads the profile directly rather than {@link access}, which answers a different question
     * (may this member use the app) and flattens every entitling status into one `entitled`.
     */
    get trial(): TrialCountdown | undefined {
      const subscription = userProfileState.profile?.subscription;
      if (subscription?.status !== 'trialing' || !subscription.currentPeriodEnd) return;

      const daysRemaining = daysUntil(subscription.currentPeriodEnd);
      // Past its end and still `trialing` means the webhook has not landed yet; say nothing rather
      // than count down from a negative number.
      if (daysRemaining === undefined || daysRemaining < 0) return;

      const remaining = `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}`;
      const totalDays = subscription.trialDays;
      return {
        daysRemaining,
        endsAt: subscription.currentPeriodEnd,
        fractionRemaining: totalDays ? Math.min(daysRemaining / totalDays, 1) : 1,
        label: daysRemaining === 0 ? 'Your free trial ends today' : `${remaining} left on your free trial`,
      };
    },

    /**
     * A renewal, or the first charge after a trial, has failed and Stripe is retrying it.
     *
     * Only `past_due`: the member still has the app while Stripe retries, so this is the window to
     * tell them before it gives up. Once it does the status moves to `unpaid` or `canceled`, which
     * {@link access} locks — the lock screens explain those, not this. A comped plan has no charge
     * to fail, so it never qualifies.
     */
    get hasFailedPayment(): boolean {
      const subscription = userProfileState.profile?.subscription;
      return subscription?.status === 'past_due' && planRequiresPayment(subscription.plan);
    },

    /** True once the card fields can be rendered. */
    get canCollect(): boolean {
      return this.phase === 'collecting' || this.phase === 'confirming';
    },

    reset() {
      this.phase = 'idle';
      this.clientSecret = undefined;
      this.customerSessionClientSecret = undefined;
      this.error = undefined;
      this.trialEligible = undefined;
      this.hasReportedCheckout = false;
      // The offered plans and the selection are not cleared: they describe what is on sale and what
      // the member picked, neither of which this attempt owns.
    },

    /**
     * Open the card collection.
     *
     * Called after the account and profile exist, because the collection needs a Stripe customer and
     * that is minted during profile creation. Failing here is reported inline rather than dropping
     * the member into the app: the profile already records that payment is owed, so the recoverable
     * outcome is telling them, not pretending it worked.
     */
    async preparePayment(): Promise<boolean> {
      this.phase = 'preparing';
      this.error = undefined;
      try {
        const result = await flows.startMembershipPayment({});

        // Stripe already entitles this member — a dropped webhook had left the profile saying
        // otherwise. The server has repaired the projection, so the only thing to do is let them in.
        if (result.data.outcome === 'already_active') {
          this.phase = 'active';
          return true;
        }

        this.clientSecret = result.data.clientSecret;
        // Reassigned rather than left alone, because a reopened collection carries its own session
        // and a stale secret from the previous one would be rejected by Stripe on mount.
        this.customerSessionClientSecret = result.data.customerSessionClientSecret;
        this.trialEligible = result.data.trialEligible;
        this.phase = 'collecting';
        // Once per checkout, not per collection: a declined charge reopens the collection, and
        // reporting again there would count every decline as a fresh checkout in GA4's funnel.
        if (!this.hasReportedCheckout) {
          trackBeginCheckout({ itemCategory: 'membership' });
          this.hasReportedCheckout = true;
        }
        return true;
      } catch (error) {
        console.error('[MembershipPaymentState] Could not start membership payment', error);
        this.phase = 'failed';
        this.error = GENERIC_PREPARE_FAILURE;
        return false;
      }
    },

    /**
     * Send a lapsed member to Stripe's billing portal.
     *
     * Their membership needs paying or repairing rather than replacing, so this is their route
     * rather than the card form — and it is a full-page navigation to Stripe, which is why the
     * platform does the navigating: the store cannot touch `window`.
     *
     * Returns the URL rather than navigating, so the same action serves the web shell and the
     * mobile one (which opens it in a browser sheet instead).
     */
    async openBillingPortal(): Promise<string | undefined> {
      this.error = undefined;
      this.isOpeningPortal = true;
      try {
        const result = await flows.openBillingPortal({});
        return result.data.url;
      } catch (error) {
        console.error('[MembershipPaymentState] Could not open the billing portal', error);
        this.error = 'We could not open your billing settings. Please try again, or contact us.';
        return;
      } finally {
        this.isOpeningPortal = false;
      }
    },

    /** True while a background membership re-read is in flight, so it cannot be started twice. */
    isRefreshing: false,

    /**
     * Re-read the membership from Stripe and let the projection repair itself.
     *
     * The lock a member sees is derived from `profile.subscription`, which the Stripe webhook writes.
     * A delayed or dropped delivery therefore locks somebody who has actually paid — and on web that
     * repairs itself as a side effect, because the payment page calls `StartMembershipPayment`, which
     * reports `already_active` and fixes the projection on its way past. Native has no payment page
     * to pass through, so without this a stale projection would hold a paying member out of the app
     * until they next opened the web app. `SyncMembership` is the flow written for exactly this (its
     * own docs call it "the account lock's check again"), and it is exempt from the lock gate so a
     * locked member can reach it.
     *
     * Returns nothing and sets no phase: the repair lands on the profile, and the profile listener is
     * what flips `access` and lifts the gate. Failures are swallowed to a warning on purpose — this
     * runs behind a screen that is already telling the member what is wrong, and "billing is not
     * configured" or "no Stripe customer yet" are both answers that would only add noise to it. A
     * member who is genuinely lapsed simply stays where they are.
     */
    async refreshMembership(): Promise<void> {
      if (this.isRefreshing) return;
      this.isRefreshing = true;
      try {
        await flows.syncMembership({});
      } catch (error) {
        console.warn('[MembershipPaymentState] Could not re-read the membership', error);
      } finally {
        this.isRefreshing = false;
      }
    },

    /** The card surface is handing control to Stripe. */
    markConfirming() {
      this.phase = 'confirming';
      this.error = undefined;
    },

    /**
     * Stripe declined or errored while STORING the card. Its own message is surfaced verbatim —
     * "your card was declined" is far more actionable than anything generic we could substitute, and
     * the member can fix it by trying another card without leaving the form. Nothing was created in
     * Stripe, and the SetupIntent was not consumed (it did not succeed), so the same collection is
     * still good for the next attempt — unlike a declined CHARGE, see `completePayment`.
     */
    failConfirmation(message: string) {
      this.phase = 'collecting';
      this.error = message;
    },

    /**
     * The card is stored; create the membership.
     *
     * One call, and the answer is final: the server creates the subscription against this card and
     * projects the status it gets back, so a `true` here means entitled — no polling, and no window
     * where a charged member is not yet a member.
     *
     * Returns false for the two outcomes that are not failures of ours: a declined first charge
     * (nothing was created; another card will work) and a second authentication demanded by the
     * issuer, which the card surface finishes with the secret in {@link pendingActionSecret}.
     */
    pendingActionSecret: undefined as string | undefined,

    async completePayment(setupIntentId: string): Promise<boolean> {
      this.phase = 'activating';
      this.error = undefined;
      this.pendingActionSecret = undefined;
      try {
        const result = await flows.completeMembershipPayment({
          setupIntentId,
          ...(this.planId ? { planId: this.planId } : {}),
        });

        if (result.data.outcome === 'active') {
          this.phase = 'active';
          return true;
        }

        if (result.data.outcome === 'authentication_required') {
          // The member's bank wants a challenge for the first charge. They are still on the screen,
          // so the card surface confirms this secret and calls back in.
          this.pendingActionSecret = result.data.clientSecret;
          this.phase = 'confirming';
          return false;
        }

        // Declined. The card was stored — that part succeeded — and only the charge failed, which
        // means the SetupIntent is SPENT: a second card cannot be confirmed against it, and a Payment
        // Element left mounted on it renders nothing. So open a fresh collection first, and only
        // then show Stripe's message over the new fields. Nothing was created in Stripe by the
        // decline itself, so this is a clean retry.
        const declined = result.data.message;
        if (await this.preparePayment()) this.error = declined;
        return false;
      } catch (error) {
        console.error('[MembershipPaymentState] Could not complete membership payment', error);
        this.phase = 'failed';
        // Deliberately not "your payment failed": with `error_if_incomplete` a failure here means no
        // subscription was created and no money moved, so the honest instruction is to try again.
        this.error = 'We could not start your membership. Your card has not been charged — please try again.';
        return false;
      }
    },

    /**
     * The first charge was authenticated in the browser. Read Stripe back to establish entitlement.
     *
     * Only reachable from `authentication_required`. Uses `SyncMembership` because the subscription
     * already exists by then — this is the one place the old read-back-from-Stripe step survives,
     * and it is a single call rather than a poll.
     */
    async markAuthenticated(): Promise<boolean> {
      this.phase = 'activating';
      this.pendingActionSecret = undefined;
      try {
        const result = await flows.syncMembership({});
        if (result.data.settled && isEntitlingStatus(result.data.status)) {
          this.phase = 'active';
          return true;
        }
      } catch (error) {
        console.warn('[MembershipPaymentState] Could not read the membership back', error);
      }
      this.phase = 'failed';
      this.error =
        'Your payment went through and your membership is being set up. Nothing needs paying again — ' +
        'if this screen is still here in a few minutes, contact us and we will finish it by hand.';
      return false;
    },
  });
}

export type MembershipPaymentState = ReturnType<typeof createMembershipPaymentState>;
